import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  LogOut, Menu, Loader2, Plus, Trash2, Globe, LayoutDashboard,
  Workflow, BarChart3, Activity, Store, Shield, Settings, Users,
  Building2, ChevronRight, RefreshCw, CheckCircle2, Database, Play
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Businesses = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', website_url: '', industry: 'general', tools: '', business_hours: '', tone_preferences: '' });
  const [summarizing, setSummarizing] = useState('');

  useEffect(() => { fetchBusinesses(); }, []);

  const fetchBusinesses = async () => {
    try { const r = await axios.get(`${API}/businesses`); setBusinesses(r.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.name) return toast.error('Business name required');
    try {
      await axios.post(`${API}/businesses`, { ...form, tools: form.tools.split(',').map(t => t.trim()).filter(Boolean) });
      toast.success('Business created');
      setShowCreate(false);
      setForm({ name: '', website_url: '', industry: 'general', tools: '', business_hours: '', tone_preferences: '' });
      fetchBusinesses();
    } catch { toast.error('Failed'); }
  };

  const handleSummarize = async (bizId) => {
    setSummarizing(bizId);
    try {
      const res = await axios.post(`${API}/businesses/${bizId}/summarize-website`);
      toast.success('Website summarized');
      fetchBusinesses();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to summarize'); }
    finally { setSummarizing(''); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this business profile?')) return;
    try { await axios.delete(`${API}/businesses/${id}`); fetchBusinesses(); toast.success('Deleted'); }
    catch { toast.error('Failed'); }
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Building2, label: 'Businesses', path: '/businesses' },
    { icon: Workflow, label: 'Workflow Builder', path: '/workflow' },
    { icon: Store, label: 'Marketplace', path: '/marketplace' },
    { icon: Activity, label: 'Agent Monitor', path: '/agents' },
    { icon: Shield, label: 'Operations', path: '/operations' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="noise-overlay" />
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0a0a0a] border-r border-white/5 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5"><Link to="/" className="flex items-center gap-2"><HSLogo size={32} /><span className="font-black text-lg tracking-tight">HumanScaled</span></Link></div>
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map(item => { const Icon = item.icon; const isActive = location.pathname === item.path; return (<Link key={item.path} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${isActive ? 'bg-emerald-500/10 text-emerald-400 border-l-2 border-emerald-500' : 'text-muted-foreground hover:text-white hover:bg-white/5'}`}><Icon className="w-5 h-5" />{item.label}</Link>); })}
          </nav>
          <div className="p-4 border-t border-white/5">
            <Button variant="ghost" onClick={() => { logout(); navigate('/'); }} className="w-full justify-start text-muted-foreground hover:text-white"><LogOut className="w-4 h-4 mr-2" /> Sign Out</Button>
          </div>
        </div>
      </aside>
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-muted-foreground hover:text-white"><Menu className="w-6 h-6" /></button>
              <h1 className="text-lg font-bold" data-testid="businesses-title">Business Profiles</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => navigate('/businesses/onboard')} variant="outline" className="border-emerald-500/20 text-emerald-400 rounded-sm h-9 text-sm" data-testid="onboard-btn"><Play className="w-4 h-4 mr-1" /> Onboard Business</Button>
              <Button onClick={() => setShowCreate(true)} className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-9 text-sm" data-testid="new-biz-btn"><Plus className="w-4 h-4 mr-1" /> Quick Add</Button>
            </div>
          </div>
        </header>

        <div className="p-6 lg:p-8">
          {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div> : businesses.length === 0 ? (
            <div className="bg-[#0a0a0a] border border-white/5 p-12 text-center">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
              <h3 className="text-lg font-bold mb-2">No business profiles yet</h3>
              <p className="text-sm text-muted-foreground mb-4">Add a business to get tailored workflow suggestions and context-aware commands.</p>
              <Button onClick={() => setShowCreate(true)} className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm">Add Business</Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {businesses.map(biz => (
                <div key={biz.id} className="bg-[#0a0a0a] border border-white/5 p-5 hover:border-emerald-500/20 transition-colors group" data-testid={`biz-${biz.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold">{biz.name}</h3>
                      <span className="text-xs text-muted-foreground capitalize">{biz.industry}</span>
                    </div>
                    <button onClick={() => handleDelete(biz.id)} className="opacity-0 group-hover:opacity-100 p-1 text-muted-foreground hover:text-red-400 transition-all"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  {biz.website_url && (
                    <div className="flex items-center gap-2 mb-2">
                      <Globe className="w-3 h-3 text-muted-foreground" />
                      <a href={biz.website_url} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-400 hover:underline truncate">{biz.website_url}</a>
                      <Button onClick={() => handleSummarize(biz.id)} disabled={summarizing === biz.id} variant="ghost" size="sm" className="h-5 px-1.5 text-[10px] text-muted-foreground">
                        {summarizing === biz.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      </Button>
                    </div>
                  )}
                  {biz.website_summary && <p className="text-xs text-muted-foreground mb-2 line-clamp-3">{biz.website_summary.slice(0, 150)}...</p>}
                  {biz.tools?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {biz.tools.map(t => <span key={t} className="text-[10px] px-1.5 py-0.5 bg-white/5 text-muted-foreground">{t}</span>)}
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t border-white/5 mt-2">
                    <span className="text-[10px] text-muted-foreground">{new Date(biz.created_at).toLocaleDateString()}</span>
                    <Button onClick={() => navigate(`/businesses/${biz.id}/knowledge`)} size="sm" className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-sm h-6 text-[10px] px-2"><Database className="w-3 h-3 mr-0.5" /> Knowledge</Button>
                    <Button onClick={() => navigate(`/workflow`)} size="sm" variant="outline" className="border-white/10 rounded-sm h-6 text-[10px] px-2">Build Workflow <ChevronRight className="w-3 h-3 ml-0.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-md">
          <DialogHeader><DialogTitle>Add Business Profile</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Business Name" className="bg-black/50 border-white/10 rounded-sm" data-testid="biz-name" />
            <Input value={form.website_url} onChange={e => setForm({ ...form, website_url: e.target.value })} placeholder="https://company.com" className="bg-black/50 border-white/10 rounded-sm" data-testid="biz-url" />
            <Select value={form.industry} onValueChange={v => setForm({ ...form, industry: v })}>
              <SelectTrigger className="bg-black/50 border-white/10 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-white/10">
                {['general', 'construction', 'healthcare', 'insurance', 'realestate', 'legal', 'ecommerce', 'marketing'].map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input value={form.tools} onChange={e => setForm({ ...form, tools: e.target.value })} placeholder="Tools (comma-separated): CRM, Calendar, Email" className="bg-black/50 border-white/10 rounded-sm" />
            <Input value={form.business_hours} onChange={e => setForm({ ...form, business_hours: e.target.value })} placeholder="Business hours: M-F 9am-5pm" className="bg-black/50 border-white/10 rounded-sm" />
            <Input value={form.tone_preferences} onChange={e => setForm({ ...form, tone_preferences: e.target.value })} placeholder="Tone: Professional, Friendly" className="bg-black/50 border-white/10 rounded-sm" />
            <Button onClick={handleCreate} className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm" data-testid="create-biz-submit">Create Business</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Businesses;
